import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  HStack,
  MenuList,
  MenuItem,
  Text,
  VStack
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ObservationEditProto, ObservationEditType } from "build-ts/analysis_plan";
import { SuggestionMap, Suggestions } from "./dataTableUtils";
import { useAnalysisTreeContext } from "context/AnalysisTreeContext";
import { NodeInsights } from "containers/Interfaces";

interface EditTreeSuggestionsProps {
  node: NodeInsights;
  listOfSuggestions: Suggestions;
  newSuggestionMap: SuggestionMap;
  setNewSuggestionMap: (newSuggestionMap: SuggestionMap) => void;
  selectedRowIndex: number | null;
  melted: boolean;
  handleMenuClose: () => void;
}

export const EditTreeSuggestions: React.FC<EditTreeSuggestionsProps> = ({
    node,
    listOfSuggestions,
    newSuggestionMap,
    setNewSuggestionMap,
    selectedRowIndex,
    melted,
    handleMenuClose
}) => {
    const [selectedHeaders, setSelectedHeaders] = useState<Header[]>([]);
    const {transactionId, onEdgeClickMKG } = useAnalysisTreeContext();

  // Function to handle header selection
  const handleHeaderSelection = (header: Header) => {
    if (!header.id) return;
    
    const newMap = { ...newSuggestionMap };
    const currentItem = newMap[header.id];
    
    if (currentItem) {
      // Toggle selection state
      newMap[header.id] = {
        ...currentItem,
        isSelected: !currentItem.isSelected
      };
      
      // Update selected headers
      const selectedHeaders: Header[] = [];
      Object.entries(newMap).forEach(([_, item]) => {
        if (item.isSelected) {
          selectedHeaders.push(header);
        }
      });
      setSelectedHeaders(selectedHeaders);
      setNewSuggestionMap(newMap);
    }
  };

  // Function to handle generate action
  const handleGenerate = async () => {
    handleMenuClose();

    const changes: { header: Header; isAdd: boolean }[] = [];
    
    // Loop through newSuggestionMap to find changes
    Object.entries(newSuggestionMap).forEach(([id, item]) => {
      if (item.isSelected !== item.isPresent) {
        const header = item.observationColumnProto?.kg_column_header || 
                      item.metricProto?.header || 
                      item.benchmarkEntityProto?.header;
        if (header) {
          changes.push({
            header,
            isAdd: item.isSelected
          });
        }
      }
    });

    // Create ObservationEdits
    const observationEdits: ObservationEditProto[] = [];
    
    // Process each change
    changes.forEach(({ header, isAdd }) => {
      const item = newSuggestionMap[header.id!];
      if (!item) return;
     
      if(item.observationColumnProto){
        const columnEdit = ObservationEditProto.create({
          edit_type: isAdd ? ObservationEditType.ADD_COLUMN : ObservationEditType.REMOVE_COLUMN,
          is_recommendation: true,
          column: item.observationColumnProto
        });
        observationEdits.push(columnEdit);
      }
      else if(item.benchmarkEntityProto){
        const benchmarkEdit = ObservationEditProto.create({
          edit_type: isAdd ? ObservationEditType.ADD_BENCHMARK : ObservationEditType.REMOVE_BENCHMARK,
          is_recommendation: true,
          benchmark: item.benchmarkEntityProto
        });
        observationEdits.push(benchmarkEdit);
      } else if(item.metricProto){
        const metricEdit = ObservationEditProto.create({
          edit_type: isAdd ? ObservationEditType.ADD_METRIC : ObservationEditType.REMOVE_METRIC,
          is_recommendation: true,
          metric: item.metricProto
        });
        observationEdits.push(metricEdit);
      }
    });

    if (melted) {
      onEdgeClickMKG(node, 0, transactionId, observationEdits);
    } else {
      onEdgeClickMKG(node, selectedRowIndex!, transactionId, observationEdits);
    }
    
    // Reset selections based on presence
    const newMap = { ...newSuggestionMap };
    Object.keys(newMap).forEach(key => {
      newMap[key] = {
        ...newMap[key],
        isSelected: newMap[key].isPresent
      };
    });
    setNewSuggestionMap(newMap);
  };

  return (
    <MenuList maxH="300px" overflowY="auto" minW="250px" zIndex={1600}>
      {Object.entries(listOfSuggestions).some(([_, headers]) => headers.length > 0) ? (
        <VStack align="stretch" spacing={2} p={2}>
          {/* Render Attributes */}
          {listOfSuggestions.attributes.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} pl={2}>
                Attributes
              </Text>
              {listOfSuggestions.attributes.map((header) => (
                <MenuItem 
                  key={header.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeaderSelection(header);
                  }}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={3}>
                    <Checkbox 
                      isChecked={header.name ? (newSuggestionMap[header?.id!]?.isSelected ?? false) : false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleHeaderSelection(header);
                      }}
                      colorScheme="purple"
                    />
                    <Text fontSize="sm">{header.name}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Render Measures */}
          {listOfSuggestions.measures.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} pl={2}>
                Measures
              </Text>
              {listOfSuggestions.measures.map((header) => (
                <MenuItem
                  key={header.id}
                  onClick={(e)=>{
                    e.stopPropagation();
                    handleHeaderSelection(header);
                  }}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={3}>
                    <Checkbox 
                      isChecked={header.name ? (newSuggestionMap[header?.id!]?.isSelected ?? false) : false}
                      onChange={(e)=>{
                        e.stopPropagation();
                        handleHeaderSelection(header);
                      }}
                      colorScheme="purple"
                    />
                    <Text fontSize="sm">{header.name}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Render Metric Edges */}
          {listOfSuggestions.metricEdges.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} pl={2}>
                Metric Edges
              </Text>
              {listOfSuggestions.metricEdges.map((header) => (
                <MenuItem 
                  key={header.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeaderSelection(header);
                  }}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={3}>
                    <Checkbox 
                      isChecked={header.name ? (newSuggestionMap[header?.id!]?.isSelected ?? false) : false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleHeaderSelection(header);
                      }}
                      colorScheme="purple"
                    />
                    <Text fontSize="sm">{header.name}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Render Metrics */}
          {listOfSuggestions.metrics.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} pl={2}>
                Metrics
              </Text>
              {listOfSuggestions.metrics.map((header) => (
                <MenuItem 
                  key={header.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeaderSelection(header);
                  }}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={3}>
                    <Checkbox 
                      isChecked={header.name ? (newSuggestionMap[header?.id!]?.isSelected ?? false) : false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleHeaderSelection(header);
                      }}
                      colorScheme="purple"
                    />
                    <Text fontSize="sm">{header.name}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Render Benchmarks */}
          {listOfSuggestions.benchmarks.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} pl={2}>
                Benchmarks
              </Text>
              {listOfSuggestions.benchmarks.map((benchmark) => (
                <MenuItem 
                  key={benchmark.header?.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeaderSelection(benchmark.header!);
                  }}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={3}>
                    <Checkbox
                      isChecked={benchmark.header?.name ? (newSuggestionMap[benchmark.header?.id!]?.isSelected ?? false) : false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleHeaderSelection(benchmark.header!);
                      }}
                      colorScheme="purple"
                    />
                    <Text fontSize="sm">{benchmark.header?.name}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Render Temporal */}
          {listOfSuggestions.temporal.length > 0 && (
            <Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1} pl={2}>
                Temporal
              </Text>
              {listOfSuggestions.temporal.map((header) => (
                <MenuItem 
                  key={header.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHeaderSelection(header);
                  }}
                  _hover={{ bg: "gray.100" }}
                >
                  <HStack spacing={3}>
                    <Checkbox 
                      isChecked={header.name ? (newSuggestionMap[header?.id!]?.isSelected ?? false) : false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleHeaderSelection(header);
                      }}
                      colorScheme="purple"
                    />
                    <Text fontSize="sm">{header.name}</Text>
                  </HStack>
                </MenuItem>
              ))}
            </Box>
          )}

          {selectedHeaders.length > 0 && (
            <Box pt={2} borderTop="1px" borderColor="gray.200">
              <Button
                size="sm"
                colorScheme="purple"
                width="100%"
                onClick={handleGenerate}
              >
                Generate ({selectedHeaders.length} selected)
              </Button>
            </Box>
          )}
        </VStack>
      ) : (
        <MenuItem fontSize="sm" fontWeight={600} isDisabled>
          No suggestions available
        </MenuItem>
      )}
    </MenuList>
  );
};